import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { Provider } from '@atlaskit/collab-provider/provider';
import { cssMap } from '@atlaskit/css';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';

import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	createHookWithAssistanceService,
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type { Agent } from '@atlassian/conversation-assistant-service/types';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';

import { AgentPresenceMenu } from './AgentPresenceMenu.tsx';
import { AgentPresenceAvatarGroup } from './AgentPresenceAvatarGroup.tsx';
import type { AgentWithPresence } from './AgentPresenceTypes.ts';
import { getAgentPresenceStatus, getNextAgentPresenceBoundary } from './agentPresenceStatus.ts';

export type AgentPresenceProps = {
	isNestedInPresenceAvatarGroup?: boolean;
	onActiveAgentsChange?: (agents: AgentWithPresence[]) => void;
	/**
	 * Presence-only provider for the view route, injected by `PresenceAvatarGroup`. Non-null only
	 * when the page presence connection exists and the agent-view gate is on, so its presence is
	 * what identifies view mode here — no `contentMode` prop is needed.
	 */
	presenceProvider?: Provider | null;
};

type AgentPresenceMetadata = Pick<
	AgentWithPresence,
	'actingUserId' | 'permit' | 'presenceActivity' | 'presenceId' | 'lastActive' | 'presenceStatus'
>;

const hasValidLastActive = (lastActive: number | undefined): lastActive is number =>
	typeof lastActive === 'number' && Number.isFinite(lastActive);

const isMoreRecentPresence = (
	current: Pick<AgentWithPresence, 'lastActive'>,
	previous: Pick<AgentWithPresence, 'lastActive'>,
): boolean =>
	hasValidLastActive(current.lastActive) &&
	(!hasValidLastActive(previous.lastActive) || current.lastActive > previous.lastActive);

const i18n = defineMessages({
	agentGroupLabel: {
		id: 'team-presence.agent-group.label',
		defaultMessage: 'Active agents on content',
		description: 'Accessible region landmark for agent presence avatar group',
	},
	externalAgentName: {
		id: 'team-presence.external-agent.name',
		defaultMessage: 'External agent',
		description: 'Display name for an external agent whose details could not be resolved',
	},
});

const styles = cssMap({
	nestedTrigger: {
		display: 'inline-flex',
		alignItems: 'center',
	},
});

const getUniqueAgents = (agents: (AgentWithPresence | undefined)[]) => {
	const uniqueAgents = new Map<string, AgentWithPresence>();

	agents?.forEach((agent) => {
		if (agent) {
			uniqueAgents.set(agent.id, agent);
		}
	});

	return Array.from(uniqueAgents.values());
};

const getUniqueAgentIds = (agentIds: string[]) => Array.from(new Set(agentIds)).sort();

const getAgentIdFromParticipantUserId = (userId: string) => userId.replace(/^agent:/, '');

const areAgentPresenceMetadataMapsEqual = (
	previousPresenceById: Map<string, AgentPresenceMetadata>,
	nextPresenceById: Map<string, AgentPresenceMetadata>,
) => {
	if (previousPresenceById.size !== nextPresenceById.size) {
		return false;
	}

	return Array.from(nextPresenceById).every(([agentId, nextPresence]) => {
		const previousPresence = previousPresenceById.get(agentId);

		return (
			previousPresence?.actingUserId === nextPresence.actingUserId &&
			previousPresence?.permit?.isPermittedToEdit === nextPresence.permit?.isPermittedToEdit &&
			// Compared as well as `isPermittedToEdit`, so a view-permit transition re-renders. Without
			// this an agent gaining or losing view permission would be invisible on the view route,
			// where it is the only permit it has.
			previousPresence?.permit?.isPermittedToView === nextPresence.permit?.isPermittedToView &&
			previousPresence?.presenceActivity === nextPresence.presenceActivity &&
			previousPresence?.presenceId === nextPresence.presenceId &&
			previousPresence?.lastActive === nextPresence.lastActive &&
			previousPresence?.presenceStatus === nextPresence.presenceStatus
		);
	});
};

const updateCachedAgentsById = (
	previousAgentsById: Map<string, AgentWithPresence>,
	activeAgentIds: string[],
	agentEntries: Iterable<readonly [string, AgentWithPresence]>,
) => {
	const activeAgentIdSet = new Set(activeAgentIds);
	let nextAgentsById: Map<string, AgentWithPresence> | undefined;

	for (const [agentId, agent] of agentEntries) {
		if (activeAgentIdSet.has(agentId) && previousAgentsById.get(agentId) !== agent) {
			nextAgentsById ??= new Map(previousAgentsById);
			nextAgentsById.set(agentId, agent);
		}
	}

	previousAgentsById.forEach((_agent, agentId) => {
		if (!activeAgentIdSet.has(agentId)) {
			nextAgentsById ??= new Map(previousAgentsById);
			nextAgentsById.delete(agentId);
		}
	});

	return nextAgentsById ?? previousAgentsById;
};

type UseAgentsByIdsProps = {
	agentIds: string[];
} & AssistanceServiceInjectedParams;

const useActiveAgentPresence = (
	presenceProvider?: Provider | null,
): {
	activeAgentIds: string[];
	activeAgentPresenceById: Map<string, AgentPresenceMetadata>;
} => {
	const [{ collabEditProvider }] = useNativeCollabState();
	const [activeAgentIds, setActiveAgentIds] = useState<string[]>([]);
	const [activeAgentPresenceById, setActiveAgentPresenceById] = useState(
		() => new Map<string, AgentPresenceMetadata>(),
	);
	const lifecycleTimeoutRef = useRef<number | undefined>(undefined);

	// The presence-only provider wins when it exists. It is created for the viewed page, whereas the
	// native-collab store is not scoped by contentId, so an edit provider left over from another
	// content item could otherwise supply the agent list on a published page.
	const provider = presenceProvider ?? collabEditProvider;

	useEffect(() => {
		const clearLifecycleTimeout = () => {
			if (lifecycleTimeoutRef.current !== undefined) {
				window.clearTimeout(lifecycleTimeoutRef.current);
				lifecycleTimeoutRef.current = undefined;
			}
		};

		const updateActiveAgentIds = (agentIds: string[]) => {
			setActiveAgentIds((prev) => {
				const next = getUniqueAgentIds(agentIds);
				return prev.length === next.length && prev.every((id, i) => id === next[i]) ? prev : next;
			});
		};

		// Only agents that may actually be shown. An agent permitted neither to view nor edit is not
		// rendered — but that filter applies solely to the presence-only provider, because
		// `buildAIProviderPresencePayload` in collab-provider zeroes every permit key for agents
		// upserted locally on the editor route. Filtering those would hide agents that render today.
		const getRenderableAIProviderParticipants = () => {
			const aiProviderParticipants = provider?.getAIProviderParticipants() ?? [];

			if (!presenceProvider) {
				return aiProviderParticipants;
			}

			return aiProviderParticipants.filter(
				(agent) => agent.permit?.isPermittedToView || agent.permit?.isPermittedToEdit,
			);
		};

		const handleUpdatePresenceOld = () => {
			const aiProviderParticipants = getRenderableAIProviderParticipants();
			const agentIds = aiProviderParticipants.map((agent) =>
				getAgentIdFromParticipantUserId(agent.userId),
			);

			updateActiveAgentIds(agentIds);
		};

		const handleUpdatePresenceNew = () => {
			clearLifecycleTimeout();
			const now = Date.now();
			const aiProviderParticipants = getRenderableAIProviderParticipants().filter(
				(agent) => getAgentPresenceStatus(agent, now) !== 'expired',
			);
			const agentPresenceById = new Map<string, AgentPresenceMetadata>();
			let nextLifecycleBoundary: number | undefined;

			aiProviderParticipants.forEach((agent) => {
				const agentId = getAgentIdFromParticipantUserId(agent.userId);
				const lifecycleBoundary = getNextAgentPresenceBoundary(agent.lastActive, now);
				if (
					lifecycleBoundary !== undefined &&
					(nextLifecycleBoundary === undefined || lifecycleBoundary < nextLifecycleBoundary)
				) {
					nextLifecycleBoundary = lifecycleBoundary;
				}
				const agentPresence = {
					actingUserId: agent.actingUserId,
					permit: {
						isPermittedToEdit: agent.permit?.isPermittedToEdit,
						isPermittedToView: agent.permit?.isPermittedToView,
					},
					presenceActivity: agent.presenceActivity,
					presenceId: agent.presenceId ?? agent.userId,
					lastActive: agent.lastActive,
					presenceStatus: getAgentPresenceStatus(agent, now),
				} satisfies AgentPresenceMetadata;
				const previousPresence = agentPresenceById.get(agentId);
				if (!previousPresence || isMoreRecentPresence(agentPresence, previousPresence)) {
					agentPresenceById.set(agentId, agentPresence);
				}
			});

			updateActiveAgentIds(Array.from(agentPresenceById.keys()));
			setActiveAgentPresenceById((prev) =>
				areAgentPresenceMetadataMapsEqual(prev, agentPresenceById) ? prev : agentPresenceById,
			);

			if (nextLifecycleBoundary !== undefined) {
				lifecycleTimeoutRef.current = window.setTimeout(
					handleUpdatePresence,
					Math.max(0, nextLifecycleBoundary - now),
				);
			}
		};

		const handleUpdatePresence = () => {
			if (fg('platform_move_presence_agents')) {
				handleUpdatePresenceNew();
				return;
			}

			handleUpdatePresenceOld();
		};

		handleUpdatePresence();

		provider?.on('presence', handleUpdatePresence);
		if (fg('platform_move_presence_agents')) {
			provider?.on('presence:changed', handleUpdatePresence);
		}

		return () => {
			clearLifecycleTimeout();
			provider?.off('presence', handleUpdatePresence);
			if (fg('platform_move_presence_agents')) {
				provider?.off('presence:changed', handleUpdatePresence);
			}
		};
	}, [presenceProvider, provider]);

	return { activeAgentIds, activeAgentPresenceById };
};

const buildAssistanceServiceParams = (cloudId: string) => ({
	siteId: cloudId,
	product: 'confluence' as const,
	experienceId: 'ai-mate' as const,
	touchpointSource: 'aiAgentPresence' as const,
});

const AgentPresenceView = ({
	activeAgents,
	isNestedInPresenceAvatarGroup,
	onActiveAgentsChange,
}: {
	activeAgents: AgentWithPresence[];
	isNestedInPresenceAvatarGroup?: boolean;
	onActiveAgentsChange?: (agents: AgentWithPresence[]) => void;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isAgentGroupOverflowOpen, setIsAgentGroupOverflowOpen] = useState(false);

	useEffect(() => {
		if (
			!fg('platform_move_presence_agents') ||
			!isNestedInPresenceAvatarGroup ||
			!onActiveAgentsChange
		) {
			return;
		}

		onActiveAgentsChange(activeAgents);

		return () => {
			onActiveAgentsChange([]);
		};
	}, [activeAgents, isNestedInPresenceAvatarGroup, onActiveAgentsChange]);

	const handleAgentGroupOverflowMenuClick = useCallback(
		(agentCount: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'aiAgentPresence',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'aiAgentPresenceGroupOverflow',
					product: 'confluence',
					attributes: {
						agents: agentCount,
					},
				},
			}).fire();

			setIsAgentGroupOverflowOpen(
				(isAgentGroupOverflowOpenState) => !isAgentGroupOverflowOpenState,
			);
		},
		[createAnalyticsEvent, setIsAgentGroupOverflowOpen],
	);

	const handleAgentGroupOverflowMenuClose = useCallback(() => {
		setIsAgentGroupOverflowOpen(false);
	}, [setIsAgentGroupOverflowOpen]);

	const renderAgentGroupOverflowMenu = useCallback(() => {
		return <AgentPresenceMenu agents={activeAgents} />;
	}, [activeAgents]);

	useEffect(() => {
		if (!activeAgents.length) {
			setIsAgentGroupOverflowOpen(false);
		}
	}, [activeAgents.length]);

	if (!activeAgents?.length || isNestedInPresenceAvatarGroup) {
		return null;
	}

	return (
		<Popup
			placement="bottom-end"
			isOpen={isAgentGroupOverflowOpen}
			onClose={handleAgentGroupOverflowMenuClose}
			content={renderAgentGroupOverflowMenu}
			trigger={(triggerProps: TriggerProps) => (
				<Box
					{...triggerProps}
					paddingInline="space.025"
					xcss={isNestedInPresenceAvatarGroup && styles.nestedTrigger}
					aria-label={
						isNestedInPresenceAvatarGroup ? undefined : formatMessage(i18n.agentGroupLabel)
					}
					role={isNestedInPresenceAvatarGroup ? undefined : 'region'}
				>
					<AgentPresenceAvatarGroup
						agents={activeAgents}
						isOpen={isAgentGroupOverflowOpen}
						onMoreClick={() => handleAgentGroupOverflowMenuClick(activeAgents.length)}
					/>
				</Box>
			)}
		/>
	);
};

const useAgentsByIds: (
	props: Omit<UseAgentsByIdsProps, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam,
) => Agent[] = createHookWithAssistanceService(
	({ agentIds, assistanceServiceInjectedParams: { assistanceService } }: UseAgentsByIdsProps) => {
		const { data } = useQuery({
			queryKey: ['agentPresenceAgentsByIds', ...agentIds.slice().sort()],
			queryFn: async () => {
				const agentResults = await Promise.allSettled(
					agentIds.map((agentId) => assistanceService.getAgentDetails(agentId)),
				);

				return agentResults.flatMap((result) =>
					result.status === 'fulfilled' ? [result.value] : [],
				);
			},
			enabled: agentIds.length > 0,
		});

		return data ?? [];
	},
);

export const AgentPresenceComponent = ({
	isNestedInPresenceAvatarGroup,
	onActiveAgentsChange,
	presenceProvider,
}: AgentPresenceProps): React.JSX.Element | null => {
	const { cloudId } = useSessionData();
	const { activeAgentIds, activeAgentPresenceById } = useActiveAgentPresence(presenceProvider);

	const agentsFromAssistanceService = useAgentsByIds({
		agentIds: activeAgentIds,
		assistanceServiceParams: buildAssistanceServiceParams(cloudId),
	});

	const [agentsById, setAgentsById] = useState(() => new Map<string, AgentWithPresence>());

	useEffect(() => {
		setAgentsById((previousAgentsById) => {
			return updateCachedAgentsById(
				previousAgentsById,
				activeAgentIds,
				agentsFromAssistanceService.map((agent) => [agent.id, agent] as const),
			);
		});
	}, [activeAgentIds, agentsFromAssistanceService]);

	const activeAgents = useMemo(() => {
		return getUniqueAgents(
			activeAgentIds.map((agentId) => {
				const agent =
					agentsFromAssistanceService.find((agent) => agent.id === agentId) ??
					agentsById.get(agentId);

				return agent
					? {
							...agent,
							...activeAgentPresenceById.get(agentId),
						}
					: agent;
			}),
		);
	}, [activeAgentIds, activeAgentPresenceById, agentsById, agentsFromAssistanceService]);

	return (
		<AgentPresenceView
			activeAgents={activeAgents}
			isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
			onActiveAgentsChange={onActiveAgentsChange}
		/>
	);
};

const createFallbackAgent = (agentId: string, name: string): AgentWithPresence => ({
	id: agentId,
	identity_account_id: null,
	named_id: agentId,
	name,
	description: null,
	creator_type: 'CUSTOMER',
	available_plugins: {},
	is_default: false,
	actor_type: 'AGENT',
	user_defined_conversation_starters: null,
	favourite: false,
	deactivated: false,
	isExternalAgentFallback: true,
});

type UseAgentsByIdsWithAgentEditsProps = UseAgentsByIdsProps & {
	fallbackAgentName: string;
};

const useAgentsByIdsWithAgentEdits: (
	props: Omit<UseAgentsByIdsWithAgentEditsProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => Map<string, AgentWithPresence> | null = createHookWithAssistanceService(
	({
		agentIds,
		fallbackAgentName,
		assistanceServiceInjectedParams: { assistanceService },
	}: UseAgentsByIdsWithAgentEditsProps) => {
		const { data } = useQuery({
			queryKey: ['agentPresenceAgentsByIds', ...agentIds.slice().sort()],
			queryFn: async () => {
				const entries = await Promise.all(
					agentIds.map(async (agentId): Promise<[string, AgentWithPresence]> => {
						try {
							return [agentId, await assistanceService.getAgentDetails(agentId)];
						} catch {
							try {
								return [
									agentId,
									await assistanceService.getAgentDetailsByIdentityAccountId(agentId),
								];
							} catch {
								return [agentId, createFallbackAgent(agentId, fallbackAgentName)];
							}
						}
					}),
				);
				return new Map(entries);
			},
			enabled: agentIds.length > 0,
		});

		return data ?? null;
	},
);

const AgentPresenceComponentWithAgentEdits = ({
	isNestedInPresenceAvatarGroup,
	onActiveAgentsChange,
	presenceProvider,
}: AgentPresenceProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { cloudId } = useSessionData();
	const { activeAgentIds, activeAgentPresenceById } = useActiveAgentPresence(presenceProvider);

	const resolvedAgentsById = useAgentsByIdsWithAgentEdits({
		agentIds: activeAgentIds,
		fallbackAgentName: formatMessage(i18n.externalAgentName),
		assistanceServiceParams: buildAssistanceServiceParams(cloudId),
	});

	const [agentsById, setAgentsById] = useState(() => new Map<string, AgentWithPresence>());

	useEffect(() => {
		setAgentsById((previous) => {
			return updateCachedAgentsById(previous, activeAgentIds, resolvedAgentsById ?? []);
		});
	}, [activeAgentIds, resolvedAgentsById]);

	const activeAgents = useMemo(
		() =>
			getUniqueAgents(
				activeAgentIds.map((agentId) => {
					const agent = resolvedAgentsById?.get(agentId) ?? agentsById.get(agentId);

					return agent
						? {
								...agent,
								...activeAgentPresenceById.get(agentId),
							}
						: agent;
				}),
			),
		[activeAgentIds, activeAgentPresenceById, agentsById, resolvedAgentsById],
	);

	return (
		<AgentPresenceView
			activeAgents={activeAgents}
			isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
			onActiveAgentsChange={onActiveAgentsChange}
		/>
	);
};

export const AgentPresence = ({
	isNestedInPresenceAvatarGroup,
	onActiveAgentsChange,
	presenceProvider,
}: AgentPresenceProps): React.JSX.Element | null => {
	const { isAdminHubAIEnabled, isAIEarlyAccessEnabled } = useSessionData();

	if (
		!isAdminHubAIEnabled ||
		!isAIEarlyAccessEnabled ||
		!expValEquals('reinstate_confluence_agent_presence', 'isEnabled', true) ||
		(isNestedInPresenceAvatarGroup && !fg('platform_move_presence_agents'))
	) {
		return null;
	}

	return (
		<ErrorBoundary attribution={Attribution.COLLABORATION_PRESENCE}>
			{expValEquals('platform_editor_agent_be_streaming', 'isEnabled', true) ? (
				<AgentPresenceComponentWithAgentEdits
					isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
					onActiveAgentsChange={onActiveAgentsChange}
					presenceProvider={presenceProvider}
				/>
			) : (
				<AgentPresenceComponent
					isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
					onActiveAgentsChange={onActiveAgentsChange}
					presenceProvider={presenceProvider}
				/>
			)}
		</ErrorBoundary>
	);
};
